import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  StatsApi,
  groupTargetingRows,
  toTargetingRows,
  type ExpressionType,
  type StatsQuery,
  type TargetingStatsRow,
} from '@/services/stats/statsApi'
import { getDateRange, type DateRange } from '@/services/stats/statsRange'
import { AuthService } from '@/services/core/auth'
import { useNetwork } from '../contexts/NetworkContext'
import type { StatMetric } from '../components/statsColumns'

/**
 * Metrics the targeting stats endpoint reports per targeting value. `ctr`,
 * `renderedRate` and `viewability` are not stored — {@link withDerivedMetrics}
 * works them out from the counts.
 */
export const TARGETING_STAT_METRICS: StatMetric[] = [
  { key: 'impressions', labelKey: 'stats.impressions', format: 'count', defaultVisible: true },
  { key: 'clicks', labelKey: 'stats.clicks', format: 'count', defaultVisible: true },
  { key: 'ctr', labelKey: 'stats.ctr', format: 'percent', defaultVisible: true },
  { key: 'viewables', labelKey: 'stats.viewables', format: 'count', defaultVisible: true },
  { key: 'viewability', labelKey: 'stats.viewability', format: 'percent', defaultVisible: true },
  { key: 'rendered', labelKey: 'stats.rendered', format: 'count' },
  // Costs are absent from the response whenever the line item has none, so
  // this stays out of the default columns rather than showing a row of dashes.
  { key: 'cost', labelKey: 'stats.cost', format: 'currency' },
  { key: 'renderedRate', labelKey: 'stats.renderedRate', format: 'percent' },
  { key: 'visibles', labelKey: 'stats.visibles', format: 'count' },
]

/** One targeting value's row in the totals table. */
export interface TargetingTotalsRow {
  type: ExpressionType
  /** The targeting value as the ad server matched it, e.g. a site name. */
  value: string
  /** Metrics keyed as {@link TARGETING_STAT_METRICS} names them. */
  stats: Record<string, unknown>
}


function rate(part: number, whole: number): number {
  return whole > 0 ? (part / whole) * 100 : 0
}

/** Adds the rates the endpoint does not store to a row's metrics. */
function withDerivedMetrics(row: TargetingStatsRow, viewabilityMethod: string): TargetingTotalsRow {
  const { impressions, clicks, rendered, viewables } = row.stats
  const viewableBase = viewabilityMethod === 'PER_IMPRESSION' ? impressions : rendered
  return {
    type: row.type,
    value: row.value,
    stats: {
      ...row.stats,
      ctr: rate(clicks, impressions),
      renderedRate: rate(rendered, impressions),
      viewability: rate(viewables, viewableBase),
    },
  }
}

/**
 * What a line item (or any other stats subject) delivered broken down by the
 * targeting it matched — one row per targeting value.
 */
export function useTargetingStats(
  query: Partial<StatsQuery>,
  range: DateRange,
  enabled = true
) {
  const { currentNetwork } = useNetwork()
  const timeZone = currentNetwork?.timeZone ?? 'UTC'
  const viewabilityMethod = currentNetwork?.viewabilityMethod ?? 'PER_IMPRESSION'
  const currency = query.currency ?? currentNetwork?.defaultCurrency ?? 'NOK'
  const hasToken = AuthService.isAuthenticated()

  const { startDate, endDate } = useMemo(() => getDateRange(range, timeZone), [range, timeZone])

  const result = useQuery({
    queryKey: ['targeting-stats', query, range, startDate, endDate, currency],
    queryFn: () =>
      StatsApi.getTargeting({
        startDate,
        endDate,
        currency,
        // Without a groupBy the endpoint returns the period's totals per
        // targeting value, which is exactly one row per table row.
        topN: 50,
        timeout: 180000,
        ...query,
      }),
    enabled: enabled && hasToken && !!currentNetwork,
  })

  // One list, but ordered so it still reads by targeting type: busiest type
  // first, and within a type the biggest value first. The table is sortable,
  // so a reader after a flat ranking can click through to one.
  const rows = useMemo((): TargetingTotalsRow[] => {
    const groups = groupTargetingRows(toTargetingRows(result.data)).map((group) => ({
      ...group,
      impressions: group.rows.reduce((sum, row) => sum + row.stats.impressions, 0),
    }))
    groups.sort((a, b) => b.impressions - a.impressions)
    return groups.flatMap((group) =>
      group.rows.map((row) => withDerivedMetrics(row, viewabilityMethod))
    )
  }, [result.data, viewabilityMethod])

  return {
    rows,
    isLoading: result.isLoading,
    error: result.error as Error | null,
  }
}
